import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from "@/lib/api";

export default function NotificationsPage() {
  const [data, setData] = useState({ notifications: [], unread_count: 0 });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await getNotifications();
      setData(res);
      setError(null);
    } catch (err) {
      setError(err?.response?.status === 401
        ? "Connect your wallet session to view notifications."
        : (err?.response?.data?.detail || "Could not load notifications"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onRead = async (id) => {
    try {
      await markNotificationRead(id);
      load();
    } catch (_) {}
  };

  const onReadAll = async () => {
    try {
      await markAllNotificationsRead();
      load();
    } catch (_) {}
  };

  return (
    <div className="min-h-screen grain">
      <Navbar category="tokens" />
      <main className="mx-auto max-w-2xl px-4 py-8">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-600">Account</p>
            <h1 className="mt-2 font-display text-3xl font-black tracking-tight">Notifications</h1>
          </div>
          {data.unread_count > 0 && (
            <button
              type="button"
              data-testid="notifications-read-all"
              onClick={onReadAll}
              className="rounded-full border border-white/15 px-3 py-1.5 text-xs uppercase tracking-wider text-zinc-400 hover:text-white"
            >
              Mark all read
            </button>
          )}
        </div>

        {loading && <div className="mt-8 text-sm text-zinc-500">Loading…</div>}
        {error && <div className="mt-8 text-sm text-zinc-400" data-testid="notifications-error">{error}</div>}

        <div className="mt-8 space-y-2" data-testid="notifications-list">
          {(data.notifications || []).map((n) => {
            const path = n.metadata?.canonical_path || "/battles";
            const headline = n.metadata?.share?.headline || n.event_type;
            return (
              <div
                key={n.id}
                className={`rounded-xl border px-4 py-3 ${n.unread ? "border-gold/30 bg-gold/[0.03]" : "border-white/10"}`}
                data-testid={`notification-${n.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-600">{n.event_type}</div>
                    <Link to={path} className="mt-1 block text-sm text-zinc-200 hover:text-gold">
                      {headline}
                    </Link>
                    <div className="mt-1 text-[11px] text-zinc-600">
                      {n.created_at ? new Date(n.created_at).toUTCString() : ""}
                    </div>
                  </div>
                  {n.unread && (
                    <button
                      type="button"
                      className="shrink-0 text-[11px] uppercase tracking-wider text-zinc-500 hover:text-white"
                      onClick={() => onRead(n.id)}
                    >
                      Mark read
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {!loading && !error && !(data.notifications || []).length && (
            <div className="rounded-xl border border-white/10 px-4 py-8 text-sm text-zinc-500">
              No competition notifications yet.
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
